import React, { useState, useCallback, forwardRef } from 'react';
import type { FC } from 'react';
import { styled } from '@compiled/react';
import { Subscribe } from 'unstated';

import type { PopupComponentProps, TriggerProps } from '@atlaskit/popup/types';
// eslint-disable-next-line check-choreographer-usage/no-import-in-product-messages-without-choreographer
import Popup from '@atlaskit/popup';
import Blanket from '@atlaskit/blanket';
import { useAnalyticsEvents } from '@atlaskit/analytics-next';
// eslint-disable-next-line @atlaskit/design-system/no-emotion-primitives -- to be migrated to @atlaskit/primitives/compiled – go/akcss
import Box from '@atlaskit/primitives/box';
import { xcss } from '@atlaskit/primitives/xcss';

import { fg } from '@confluence/feature-gating';
import { START } from '@confluence/navdex/entry-points/NavdexPointType.ts';
import { useHardStorageEnforcement } from '@confluence/storage-enforcement/entry-points/HardEnforcement/useHardStorageEnforcement.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { PageTreeStateUpdater } from '@confluence/page-tree-refresh-state-container';
import { useSSRPlaceholderReplaceIdProp } from '@confluence/loadable';
import { useFlyoutActions } from '@confluence/flyout-store';
import { usePreloadSmartCreationModal } from '@confluence/smart-create/entry-points/usePreloadSmartCreationModal.ts';

import { useSpaceEnabledContentTypes } from '../useSpaceEnabledContentTypes.tsx';
import { usePreloadCreationMenuData } from '../usePreloadCreationMenuData.ts';

import { ContextualCreateContentButton } from './ContextualCreateContentButton.tsx';
import { ContextualCreateContentPopoverMenuLoader } from './ContextualCreateContentPopoverMenuLoader.tsx';
import { CreateContentMenuItem } from './CreateContentMenuItem.tsx';

export type ContextualCreatePopoverProps = {
	closeAllHoverPageCards?: () => void;
	isContextualCreateFocused?: boolean;
	setIsContextualCreateFocused?: (isContextualCreateFocused: boolean) => void;
	spaceKey: string;
	parentId?: string;
	source: string;
	displayTitle?: string;
	rootCreateButtonStyling?: boolean;
	// Onboarding experiment prop to display the popup despite a rerender due to state change
	shouldShowPopup?: boolean;
	setShouldShowPopup?: (shouldShowPopup: boolean) => void;
	// Note: isTreeItem takes priority over renderTrigger. If both are provided,
	// renderTrigger is ignored. Use isTreeItem for page-tree icon buttons;
	// use renderTrigger for custom trigger rendering in other contexts.
	isTreeItem?: boolean;
	onItemClick?: () => void;
	shouldRenderToParent?: boolean;
	hideFolder?: boolean;
	contentType?: string;
	renderTrigger?: (props: {
		onClick: (event: React.MouseEvent<HTMLElement>) => void;
		triggerProps: TriggerProps;
	}) => React.ReactNode;
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const TriggerContainer = styled.div({
	cursor: 'default',
});

const customPopupContainerStyles = xcss({
	zIndex: 'modal',
	backgroundColor: 'elevation.surface.overlay',
	boxShadow: 'elevation.shadow.overlay',
	borderRadius: 'radius.small',
	':focus': {
		outline: 'none',
	},
});

const CustomPopupContainer = forwardRef<HTMLDivElement, PopupComponentProps>(
	({ children, 'data-testid': dataTestId, ...props }, popupRef) => (
		<Box testId={dataTestId} {...props} ref={popupRef} xcss={customPopupContainerStyles}>
			{children}
		</Box>
	),
);

export const ContextualCreateContentPopover: FC<ContextualCreatePopoverProps> = ({
	closeAllHoverPageCards,
	isContextualCreateFocused,
	setIsContextualCreateFocused,
	spaceKey,
	parentId,
	source,
	displayTitle,
	rootCreateButtonStyling = false,
	shouldShowPopup,
	setShouldShowPopup,
	isTreeItem,
	onItemClick,
	shouldRenderToParent,
	hideFolder,
	contentType,
	renderTrigger,
}) => {
	const ssrPlaceholderIdProp = useSSRPlaceholderReplaceIdProp();
	const [isOpen, setIsOpen] = useState<boolean>(() => !!shouldShowPopup);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const { closeFlyout } = useFlyoutActions();

	const { enforceStorageLimit } = useHardStorageEnforcement({
		source: 'page-tree-contextual-create',
	});

	const isOnEditRoute = useIsEditorPage();
	const preloadCreationMenuData = usePreloadCreationMenuData();
	const preloadSmartCreationModal = usePreloadSmartCreationModal();

	const preloadCreationMenu = () => {
		preloadCreationMenuData();
		preloadSmartCreationModal();
		void ContextualCreateContentPopoverMenuLoader.preload();
	};

	const stopEvents = useCallback((event: any) => {
		event.stopPropagation();
		event.preventDefault();
	}, []);

	const handleOnClick = useCallback(
		(event: React.MouseEvent) => {
			closeFlyout();
			stopEvents(event);
		},
		[stopEvents, closeFlyout],
	);

	const onTriggerClick = useCallback(() => {
		setIsContextualCreateFocused?.(!isContextualCreateFocused);
		closeAllHoverPageCards?.();
		setIsOpen(!isOpen);
		setShouldShowPopup?.(!isOpen);

		createAnalyticsEvent({
			type: 'sendUIEvent',
			data: {
				action: 'clicked',
				actionSubject: 'contextualCreateButton',
				actionSubjectId: source,
				source: 'containerNavigation',
				attributes: {
					navdexPointType: START,
					isOnEditRoute,
				},
			},
		}).fire();
	}, [
		isOpen,
		setIsOpen,
		setShouldShowPopup,
		closeAllHoverPageCards,
		isContextualCreateFocused,
		setIsContextualCreateFocused,
		createAnalyticsEvent,
		source,
		isOnEditRoute,
	]);

	const onCloseHandler = useCallback(() => {
		// The bluring of the active element should be temporary, see this ticket for an explanation: https://hello.atlassian.net/browse/CCT-906
		const activeEle = document.activeElement;
		// @ts-ignore
		activeEle?.blur();
		setIsOpen(false);
		setShouldShowPopup?.(false);
		// This needs to be set after the focus returns to the trigger button
		setTimeout(() => {
			setIsContextualCreateFocused?.(false);
		}, 0);
	}, [setIsOpen, setShouldShowPopup, setIsContextualCreateFocused]);

	const {
		isCreateWhiteboardEnabled,
		isCreateDatabaseEnabled,
		isCreateSmartLinkEnabled,
		isCreateLiveDocEnabled,
		isCreateFolderEnabled,
		isCreateSlidesEnabled,
	} = useSpaceEnabledContentTypes(spaceKey);

	return (
		<Subscribe to={[PageTreeStateUpdater]}>
			{(pageStateUpdater: PageTreeStateUpdater) => (
				// rendering popup to parent for a11y purposes was causing issues for contextual create on tree items in the page tree
				// so popup is rendered to parent based on whether shouldRenderToParent prop is true
				// TODO: DISCO-2052 resolve this issue and render to parent for all cases
				<>
					{shouldRenderToParent && isOpen && (
						<Blanket
							testId="contextual-create-blanket"
							isTinted={false}
							onBlanketClicked={onCloseHandler}
						/>
					)}
					<Popup
						isOpen={isOpen}
						onClose={onCloseHandler}
						placement="bottom-start"
						offset={[0, 8]}
						content={() => (
							<ContextualCreateContentPopoverMenuLoader
								source={source}
								parentId={parentId}
								isCreateWhiteboardEnabled={isCreateWhiteboardEnabled}
								isCreateDatabaseEnabled={isCreateDatabaseEnabled}
								isCreateSmartLinkEnabled={isCreateSmartLinkEnabled}
								isCreateLiveDocEnabled={isCreateLiveDocEnabled}
								isCreateFolderEnabled={isCreateFolderEnabled && !hideFolder}
								isCreateSlidesEnabled={isCreateSlidesEnabled}
								onCloseMenu={onCloseHandler}
								pageStateUpdater={pageStateUpdater}
								onItemClick={onItemClick}
								parentTitle={displayTitle}
								parentContentType={contentType}
							/>
						)}
						popupComponent={shouldRenderToParent ? CustomPopupContainer : undefined}
						zIndex={
							shouldRenderToParent && fg('platform-dst-motion-uplift-popup') ? 510 : undefined
						}
						trigger={(triggerProps) => {
							// isTreeItem takes priority; renderTrigger (if also provided) is ignored here.
							let triggerContent: React.ReactNode;
							if (isTreeItem) {
								triggerContent = (
									<div {...triggerProps}>
										<CreateContentMenuItem onClick={enforceStorageLimit(onTriggerClick)} />
									</div>
								);
							} else if (renderTrigger) {
								triggerContent = renderTrigger({
									onClick: enforceStorageLimit(onTriggerClick),
									triggerProps,
								});
							} else {
								triggerContent = (
									<ContextualCreateContentButton
										onClick={enforceStorageLimit(onTriggerClick)}
										triggerProps={triggerProps}
										isContextualCreateFocused={isContextualCreateFocused}
										isOpen={isOpen}
										spaceKey={spaceKey}
										rootCreateButtonStyling={rootCreateButtonStyling}
										displayTitle={displayTitle}
									/>
								);
							}

							return (
								<TriggerContainer
									onClick={handleOnClick}
									onFocus={stopEvents}
									onBlur={stopEvents}
									data-vc="contextual-create-content-popover"
									{...ssrPlaceholderIdProp}
								>
									<span onMouseEnter={preloadCreationMenu} onFocus={preloadCreationMenu}>
										{triggerContent}
									</span>
									{!shouldRenderToParent && isOpen && (
										<Blanket
											testId="contextual-create-blanket"
											isTinted={false}
											onBlanketClicked={onCloseHandler}
										/>
									)}
								</TriggerContainer>
							);
						}}
						shouldRenderToParent={shouldRenderToParent}
						testId="contextual-create-popover"
					/>
				</>
			)}
		</Subscribe>
	);
};
